import { Box, useComputedColorScheme } from "@mantine/core"
import { useEffect, useRef } from "react"

type Node = {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
}

export function NetworkBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number | undefined>(undefined)
  const nodesRef = useRef<Node[]>([])
  const computedColorScheme = useComputedColorScheme("light")

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Get colors from CSS variables
    const getColor = (varName: string) => {
      return getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
    }

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio
      canvas.width = window.innerWidth * dpr
      canvas.height = window.innerHeight * dpr
      canvas.style.width = `${window.innerWidth}px`
      canvas.style.height = `${window.innerHeight}px`
      ctx.scale(dpr, dpr)
    }

    const initNodes = () => {
      const nodeCount = Math.floor((window.innerWidth * window.innerHeight) / 25000)
      nodesRef.current = Array.from({ length: Math.min(nodeCount, 50) }, () => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 2 + 1.5,
      }))
    }

    const drawNetwork = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
      const nodes = nodesRef.current

      // Get theme-aware colors
      const parsedOpacity = parseFloat(getColor("--network-line-opacity"))
      const lineOpacity = Number.isNaN(parsedOpacity) ? 0.12 : parsedOpacity
      const nodeGlow = getColor("--network-node-glow") ?? "rgba(150, 56, 149, 0.15)"
      const nodePurple = getColor("--network-node-purple") ?? "rgba(150, 56, 149, 0.4)"
      const nodeGold = getColor("--network-node-gold") ?? "rgba(255, 215, 0, 0.6)"

      // Update positions
      nodes.forEach((node) => {
        node.x += node.vx
        node.y += node.vy

        // Bounce off edges with padding
        if (node.x < 0 || node.x > window.innerWidth) node.vx *= -1
        if (node.y < 0 || node.y > window.innerHeight) node.vy *= -1

        // Keep in bounds
        node.x = Math.max(0, Math.min(window.innerWidth, node.x))
        node.y = Math.max(0, Math.min(window.innerHeight, node.y))
      })

      // Draw connections
      const connectionDistance = 180
      ctx.lineWidth = 1

      nodes.forEach((nodeA, i) => {
        nodes.slice(i + 1).forEach((nodeB) => {
          const dx = nodeA.x - nodeB.x
          const dy = nodeA.y - nodeB.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * lineOpacity
            ctx.strokeStyle = `rgba(150, 56, 149, ${opacity})`
            ctx.beginPath()
            ctx.moveTo(nodeA.x, nodeA.y)
            ctx.lineTo(nodeB.x, nodeB.y)
            ctx.stroke()
          }
        })
      })

      // Draw nodes with glow
      nodes.forEach((node, i) => {
        // Outer glow
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius * 4)
        gradient.addColorStop(0, nodeGlow)
        gradient.addColorStop(1, "rgba(150, 56, 149, 0)")
        ctx.fillStyle = gradient
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.radius * 4, 0, Math.PI * 2)
        ctx.fill()

        // Inner node - alternate between purple and gold
        const isPrimary = i % 5 === 0
        ctx.fillStyle = isPrimary ? nodeGold : nodePurple
        ctx.beginPath()
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
        ctx.fill()
      })

      animationRef.current = requestAnimationFrame(drawNetwork)
    }

    resizeCanvas()
    initNodes()
    drawNetwork()

    const handleResize = () => {
      resizeCanvas()
      initNodes()
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [computedColorScheme])

  return (
    <Box
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    </Box>
  )
}
